/**
 * Text fitting for AcroForm fields.
 *
 * The functions in this file mirror how pdf-lib lays out a multiline text field
 * when `form.flatten()` regenerates its appearance stream
 * (`api/form/appearances.js` -> `api/text/layout.js`). Predicting that layout
 * exactly is what lets us cut a value at the very last line that will actually
 * be visible and send only the true remainder to the addendum.
 *
 * Two facts about pdf-lib drive the whole thing:
 *   - `flatten()` draws EVERY field with the form's default font (Helvetica),
 *     no matter what font the field's /DA declares. So Helvetica is the correct
 *     measuring font even for a field whose /DA says CourierNewPS-BoldMT.
 *   - The text box is inset by (borderWidth + 1) on each side, and the line
 *     height is font.heightAtSize(fontSize) * 1.2 - not fontSize * 1.2.
 *
 * That 1pt inset is also why we wrap the text ourselves instead of letting
 * pdf-lib do it: a line is allowed to run to within 1pt of the field's right
 * edge, so on paper (and on any rasterized preview) its last glyph merges with
 * the printed border of the box and reads as a truncated word. We re-wrap at a
 * narrower width and hand pdf-lib the line breaks already baked in as "\n";
 * since `lineSplit` runs before `splitOutLines`, every segment already fits and
 * pdf-lib's own wrapping becomes a no-op.
 */

/**
 * Right-hand gutter reserved inside every multiline field, as a fraction of the
 * font size so it looks the same at any size (~3.6pt at 10pt text).
 */
const RIGHT_GUTTER_RATIO = 0.4;

/**
 * The width lines are actually wrapped at: the field's box minus the gutter.
 * Never let the gutter eat the whole box on a very narrow field.
 */
export const getWrapWidth = (boundsWidth, fontSize) =>
    Math.max(boundsWidth * 0.5, boundsWidth - fontSize * RIGHT_GUTTER_RATIO);

/**
 * Extracts the font size from a PDF field's Default Appearance (DA) string.
 * DA strings look like: "/Helv 10 Tf" or "/CourierNewPS-BoldMT 10.00 Tf 0 g"
 *
 * `isAutoSize` reports the "0 Tf" case, where pdf-lib would shrink the text to
 * fit instead of clipping it. Callers that truncate must pin the size with
 * `field.setFontSize()` so what gets rendered matches what was measured.
 */
export const parseDefaultAppearance = (field) => {
    try {
        const da = field.acroField.getDefaultAppearance();
        if (!da) return { fontSize: 10, isAutoSize: false };

        const match = da.match(/(\d+(?:\.\d+)?)\s+Tf/);
        if (match) {
            const size = parseFloat(match[1]);
            return { fontSize: size > 0 ? size : 10, isAutoSize: !(size > 0) };
        }
    } catch (e) {
        // Fallback if DA parsing fails
    }
    return { fontSize: 10, isAutoSize: false };
};

/**
 * Same normalization pdf-lib applies before laying text out (`cleanText`).
 * Applying it up front keeps our character offsets aligned with pdf-lib's,
 * and it is idempotent, so pdf-lib re-running it is a no-op.
 */
export const cleanFieldText = (text) =>
    String(text ?? "")
        .replace(/\t|\u0085|\u2028|\u2029/g, "    ")
        .replace(/[\b\v]/g, "");

/** Width of a widget's border (/BS /W), 0 when it has none. */
export const getWidgetBorderWidth = (widget) => {
    try {
        return widget.getBorderStyle()?.getWidth() ?? 0;
    } catch (e) {
        return 0;
    }
};

/**
 * The usable text box of a field widget, as pdf-lib computes it.
 */
export const getFieldTextBounds = (widget, rect) => {
    const inset = getWidgetBorderWidth(widget) + 1; // pdf-lib: borderWidth + padding, padding = 1 when not combed

    return {
        width: rect.width - inset * 2,
        height: rect.height - inset * 2,
    };
};

/**
 * Number of lines that fit in the field's box, using pdf-lib's line height.
 */
export const getFieldMaxLines = (font, fontSize, boundsHeight) => {
    const lineHeight = font.heightAtSize(fontSize) * 1.2;
    return Math.floor(boundsHeight / lineHeight);
};

// Port of pdf-lib's `lastIndexOfWhitespace` (api/text/layout.js), quirks included.
const lastIndexOfWhitespace = (line) => {
    for (let idx = line.length; idx > 0; idx--) {
        if (/\s/.test(line[idx])) return idx;
    }
    return undefined;
};

// Port of pdf-lib's `splitOutLines`, returning lengths instead of substrings.
const splitOutLine = (input, maxWidth, font, fontSize) => {
    let lastWhitespaceIdx = input.length;

    while (lastWhitespaceIdx > 0) {
        const line = input.substring(0, lastWhitespaceIdx);
        if (font.widthOfTextAtSize(line, fontSize) < maxWidth) {
            return {
                lineLength: lastWhitespaceIdx,
                remainderStart: lastWhitespaceIdx < input.length ? lastWhitespaceIdx : undefined,
            };
        }
        lastWhitespaceIdx = lastIndexOfWhitespace(line) ?? 0;
    }

    return { lineLength: input.length, remainderStart: undefined };
};

/**
 * Port of pdf-lib's `layoutMultilineText` line breaking, keeping each line's
 * offsets into the source string so callers can slice the ORIGINAL text.
 *
 * @returns {Array<{ start: number, end: number, text: string }>}
 */
export const layoutFieldLines = (text, font, fontSize, maxWidth) => {
    const source = cleanFieldText(text);
    const out = [];

    // pdf-lib: lineSplit() -> split on \n \f \r
    const segments = source.split(/[\n\f\r]/);
    let segmentStart = 0;

    segments.forEach((segment, index) => {
        if (index > 0) segmentStart += 1; // the separator consumed by split()

        let base = segmentStart;
        segmentStart += segment.length;

        let input = segment;

        for (;;) {
            const { lineLength, remainderStart } = splitOutLine(input, maxWidth, font, fontSize);
            out.push({ start: base, end: base + lineLength, text: input.slice(0, lineLength) });

            if (remainderStart === undefined) break;

            const rest = input.slice(remainderStart);
            const leadingWhitespace = rest.length - rest.replace(/^\s+/, "").length;

            base += remainderStart + leadingWhitespace;
            input = rest.trim();

            // pdf-lib keeps looping on an empty remainder, emitting one blank line.
            if (input === "") {
                out.push({ start: base, end: base, text: "" });
                break;
            }
        }
    });

    return out;
};

/**
 * Wraps text into lines that fit within a given width using precise font
 * measurement. Respects explicit newlines. Used for the text WE draw ourselves
 * on the addendum pages, where our own measurement is authoritative.
 */
export const wrapText = (text, font, fontSize, maxWidth) => {
    const lines = [];
    const paragraphs = String(text ?? "").split("\n");

    for (const paragraph of paragraphs) {
        if (paragraph === "") {
            lines.push("");
            continue;
        }

        const words = paragraph.split(/\s+/).filter(Boolean);
        let currentLine = "";

        for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word;

            if (font.widthOfTextAtSize(testLine, fontSize) > maxWidth && currentLine) {
                lines.push(currentLine);

                // A single word wider than maxWidth has to be broken by character.
                if (font.widthOfTextAtSize(word, fontSize) > maxWidth) {
                    let remaining = word;
                    while (remaining.length > 0) {
                        let end = remaining.length;
                        while (end > 1 && font.widthOfTextAtSize(remaining.slice(0, end), fontSize) > maxWidth) {
                            end--;
                        }
                        lines.push(remaining.slice(0, end));
                        remaining = remaining.slice(end);
                    }
                    currentLine = "";
                } else {
                    currentLine = word;
                }
            } else {
                currentLine = testLine;
            }
        }

        if (currentLine) lines.push(currentLine);
    }

    return lines;
};

/**
 * Splits a field value into the part that will actually be visible inside the
 * field and the remainder that must move to the addendum.
 *
 * The visible part comes back with our line breaks already baked in, so pdf-lib
 * draws it at the narrower width we measured and the text keeps a gutter from
 * the box's right border. The remainder is sliced out of the source text (the
 * inserted breaks never reach it), so its paragraphs and spacing survive intact
 * and can be re-wrapped at the addendum's own width.
 *
 * `isAutoSize` fields that do NOT overflow are left alone: pdf-lib picks its own
 * font size for them, so line breaks measured at ours would be wrong. When they
 * do overflow the caller pins the size, and baking the breaks in is safe.
 *
 * @returns {{ fitted: string, remainder: string, didOverflow: boolean }}
 */
export const splitTextForAddendum = (text, font, fontSize, maxWidth, maxLines, { isAutoSize = false } = {}) => {
    const source = cleanFieldText(text);
    if (!source) return { fitted: "", remainder: "", didOverflow: false };

    // A box too short for a single line still shows one clipped line, never zero.
    const visibleLines = Math.max(1, maxLines);

    const wrapWidth = getWrapWidth(maxWidth, fontSize);
    const lines = layoutFieldLines(source, font, fontSize, wrapWidth);
    const didOverflow = lines.length > visibleLines;

    // Re-joining the visible lines is what applies the gutter; each slice already
    // fits in `wrapWidth`, so pdf-lib will not re-wrap them.
    const keep = didOverflow ? lines.slice(0, visibleLines) : lines;
    const fitted =
        isAutoSize && !didOverflow
            ? source
            : keep.map((line) => source.slice(line.start, line.end)).join("\n");

    if (!didOverflow) return { fitted, remainder: "", didOverflow: false };

    const cut = lines[visibleLines - 1].end;

    return {
        fitted,
        // The whitespace at the cut is the break itself (a space mid-paragraph, a
        // blank line between paragraphs); it must not open the addendum section.
        remainder: source.slice(cut).replace(/^\s+/, ""),
        didOverflow: true,
    };
};

/**
 * Smallest size a single-line field may shrink to before its value is sent to
 * the addendum instead. USCIS forms get scanned and read at a glance, so a table
 * cell that drops much below this stops being reliably legible.
 */
const MIN_SINGLE_LINE_SIZE = 5.5;

/**
 * Appended in the box when the value continues in the addendum, so the cell can
 * never be read as a complete - but wrong - answer. Three ASCII periods rather
 * than "…": the Unicode ellipsis encodes to 0x85 in WinAnsi, the same byte
 * `cleanText` scrubs as a control character, and that is a needless adjacency.
 */
const OVERFLOW_MARKER = "...";

/**
 * pdf-lib collapses newlines into spaces before laying a single-line field out
 * (`mergeLines` in `layoutSinglelineText`), so we measure the same string it does.
 */
const toSingleLine = (text) => cleanFieldText(text).replace(/[\n\f\r]/g, " ");

const fitsAt = (text, font, size, boundsWidth) =>
    font.widthOfTextAtSize(text, size) <= getWrapWidth(boundsWidth, size);

/**
 * Fits a value into a single-line field, which pdf-lib neither wraps nor shrinks
 * when the /DA declares a fixed size - it just draws the whole string and lets
 * `drawTextField`'s clip path cut it, silently dropping characters.
 *
 * Two stages, which is what USCIS forms themselves imply: shrink the text down to
 * `MIN_SINGLE_LINE_SIZE` if that is enough, otherwise pin it at the floor, keep
 * the prefix that fits next to `OVERFLOW_MARKER`, and let the caller carry the
 * full value into the addendum. The size is never raised above the declared one.
 *
 * @returns {{ text: string, fontSize: number, didOverflow: boolean }}
 */
export const fitSingleLineText = (text, font, declaredSize, boundsWidth) => {
    const source = toSingleLine(text);
    const declared = declaredSize > 0 ? declaredSize : 10;

    if (!source) return { text: "", fontSize: declared, didOverflow: false };
    if (fitsAt(source, font, declared, boundsWidth)) {
        return { text: source, fontSize: declared, didOverflow: false };
    }

    // A field that already declares less than the floor sets its own: shrinking
    // may never turn into enlarging.
    const floor = Math.min(MIN_SINGLE_LINE_SIZE, declared);

    // Largest quarter-point step that fits. Quarter points keep the shrink
    // visually close to the neighbouring cells instead of jumping a whole point.
    for (let size = Math.floor(declared * 4) / 4 - 0.25; size >= floor; size -= 0.25) {
        if (fitsAt(source, font, size, boundsWidth)) {
            return { text: source, fontSize: size, didOverflow: false };
        }
    }

    const fontSize = floor;
    const wrapWidth = getWrapWidth(boundsWidth, fontSize);
    const markerWidth = font.widthOfTextAtSize(OVERFLOW_MARKER, fontSize);

    // A box too narrow for even the marker keeps plain text: signalling the
    // overflow is not worth surrendering the whole cell.
    const useMarker = markerWidth < wrapWidth;
    const available = useMarker ? wrapWidth - markerWidth : wrapWidth;

    // Never hand back an empty cell: a blank reads as "not answered", which is a
    // worse misstatement than a visibly clipped one.
    let end = source.length;
    while (end > 1 && font.widthOfTextAtSize(source.slice(0, end), fontSize) > available) end--;

    // Prefer breaking at a word boundary, unless that costs too much of the cell.
    const kept = source.slice(0, end);
    const boundary = kept.search(/\s+\S*$/);
    const onWord =
        boundary > 0 && font.widthOfTextAtSize(kept.slice(0, boundary), fontSize) >= available * 0.75
            ? kept.slice(0, boundary)
            : kept;

    // The marker is dropped rather than allowed to push the cell past its clip -
    // in a box that narrow the prefix itself is the only thing worth showing.
    const marked = `${onWord.replace(/\s+$/, "")}${OVERFLOW_MARKER}`;
    const fitsMarked = font.widthOfTextAtSize(marked, fontSize) <= wrapWidth;

    return {
        text: useMarker && fitsMarked ? marked : onWord,
        fontSize,
        didOverflow: true,
    };
};
